import React from "react";
import { Pagination } from "react-bootstrap";
import Resources from "../../types/Resources";

type PaginationControlsProps = {
    currentPage: number;
    currentPageSize: number;
    totalPages: number;
    resources: Resources;
    className?: string;
    onPageChange: (newPage: number) => void;
    onPageSizeChange: (newPageSize: number) => void;
};

const pageSizes = [5, 10, 20, 50, 100] as const;
const visiblePageIndexes = 5;

function PaginationControls(props: PaginationControlsProps) {
    const { currentPage } = props;

    const hasPreviousPage = currentPage > 1;
    const hasNextPage = currentPage < props.totalPages;

    const startingEllipsisThreshold = Math.ceil(visiblePageIndexes / 2) + 1;
    const endingEllipsisThreshold = props.totalPages - Math.ceil(visiblePageIndexes / 2);

    //
    // If the current page is exactly on the threshold for showing an ellipsis, then we show the page number instead, 
    // to avoid showing an ellipsis with only the next page number in the sequence after it, which would look odd.
    // Without this check, we could get something like this:
    // 1, ..., 2, 3, [4], 5
    // With this check, we would instead show:
    // 1, 2, 3, [4], 5
    //
    const addPage1 = currentPage === startingEllipsisThreshold && props.totalPages > visiblePageIndexes;
    const addLastPage = currentPage === endingEllipsisThreshold && props.totalPages > visiblePageIndexes;

    const showStartingEllipsis = props.totalPages > visiblePageIndexes && currentPage > startingEllipsisThreshold;
    const showEndingEllipsis = props.totalPages > visiblePageIndexes && currentPage < endingEllipsisThreshold;

    const pageIndexes = getVisiblePageIndexes(currentPage, props.totalPages, visiblePageIndexes);
    const pageIndexOptions = pageIndexes.map((i) => (
        <Pagination.Item active={currentPage === i} onClick={() => props.onPageChange(i)} key={i}>
            {i}
        </Pagination.Item>
    ));

    const pageSizeOptions = pageSizes.map((size) => (
        <option key={size} value={size}>
            {size}
        </option>
    ));

    return (
        <div className={`w-100 d-flex flex-column align-items-center ${props.className ?? ""}`}>
            <Pagination>
                {hasPreviousPage ? (
                    <Pagination.Item onClick={() => props.onPageChange(currentPage - 1)}>
                        <i className="fas fa-chevron-left"></i>
                    </Pagination.Item>
                ) : null}
                {showStartingEllipsis ? (
                    <>
                        <Pagination.Item onClick={() => props.onPageChange(1)}>1</Pagination.Item>
                        <Pagination.Ellipsis disabled />
                    </>
                ) : null}
                {addPage1 ? <Pagination.Item onClick={() => props.onPageChange(1)}>1</Pagination.Item> : null}
                {pageIndexOptions}
                {addLastPage ? (
                    <Pagination.Item onClick={() => props.onPageChange(props.totalPages)}>
                        {props.totalPages}
                    </Pagination.Item>
                ) : null}
                {showEndingEllipsis ? (
                    <>
                        <Pagination.Ellipsis disabled />
                        <Pagination.Item onClick={() => props.onPageChange(props.totalPages)}>
                            {props.totalPages}
                        </Pagination.Item>
                    </>
                ) : null}
                {hasNextPage ? (
                    <Pagination.Item onClick={() => props.onPageChange(currentPage + 1)}>
                        <i className="fas fa-chevron-right"></i>
                    </Pagination.Item>
                ) : null}
            </Pagination>
            <div>
                <label htmlFor="page-size-select">{props.resources.get("ResultsPerPage")}</label>
                <select
                    id="page-size-select"
                    className="form-select"
                    value={props.currentPageSize}
                    onChange={(e) => props.onPageSizeChange(parseInt(e.target.value))}
                >
                    {pageSizeOptions}
                </select>
            </div>
        </div>
    );
}

export default PaginationControls;

function getVisiblePageIndexes(currentPage: number, totalPages: number, maxResultLength: number): number[] {
    if (totalPages <= maxResultLength) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pageIndexOptions = [];

    const startPage = Math.max(1, currentPage - Math.floor(maxResultLength / 2));
    const endPage = Math.min(totalPages, startPage + maxResultLength - 1);
    const adjustedStartPage = Math.max(1, endPage - maxResultLength + 1);

    for (let i = adjustedStartPage; i <= endPage; i++) {
        pageIndexOptions.push(i);
    }

    return pageIndexOptions;
}
